"use client";

import { create } from "zustand";

export type CrossSurfaceRevealTarget =
  | {
      surface: "present";
      kind: "today_queue_item";
      itemId: string;
    }
  | {
      // U14: when a captured task becomes the active restart, the
      // confirmation must reveal the Restart card itself — Today
      // filters the restart out of the queue, so today_queue_item
      // would highlight nothing.
      surface: "present";
      kind: "today_restart";
      itemId: string;
    }
  | {
      surface: "schedule-plan";
      kind: "schedule_block";
      blockId: string;
      planDate?: string | null;
    }
  | {
      surface: "schedule-plan" | "goals";
      kind: "goal_focus";
      goalId: string;
    };

export type CrossSurfaceConfirmationStateKind =
  | "info"
  | "suggested"
  | "retained"
  | "committed";

export interface CrossSurfaceConfirmationTruth {
  targetKind: string;
  targetId: string;
  destinationLayer: string;
  owningService: string;
  proposalId?: string | null;
  authorityLevel?: string | null;
  requiresConfirmation?: boolean | null;
}

export interface CrossSurfaceConfirmation {
  id: number;
  state: CrossSurfaceConfirmationStateKind;
  message: string;
  ctaLabel?: string | null;
  ctaTab?: string | null;
  revealTarget?: CrossSurfaceRevealTarget | null;
  truth?: CrossSurfaceConfirmationTruth | null;
  revealNonce: number;
}

interface CrossSurfaceConfirmationState {
  confirmation: CrossSurfaceConfirmation | null;
  showConfirmation: (input: {
    state?: CrossSurfaceConfirmationStateKind;
    message: string;
    ctaLabel?: string | null;
    ctaTab?: string | null;
    revealTarget?: CrossSurfaceRevealTarget | null;
    truth?: CrossSurfaceConfirmationTruth | null;
  }) => void;
  triggerReveal: () => void;
  clearConfirmation: () => void;
}

export const useCrossSurfaceConfirmationStore = create<CrossSurfaceConfirmationState>((set) => ({
  confirmation: null,
  showConfirmation: (input) =>
    set({
      confirmation: {
        id: Date.now(),
        state: input.state ?? "info",
        message: input.message,
        ctaLabel: input.ctaLabel ?? null,
        ctaTab: input.ctaTab ?? null,
        revealTarget: input.revealTarget ?? null,
        truth: input.truth ?? null,
        revealNonce: 0,
      },
    }),
  triggerReveal: () =>
    set((state) => ({
      confirmation: state.confirmation
        ? {
            ...state.confirmation,
            revealNonce: state.confirmation.revealNonce + 1,
          }
        : null,
    })),
  clearConfirmation: () => set({ confirmation: null }),
}));
